import React, { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { motion } from "framer-motion";

export const DraftTicker = ({ sessionId }: { sessionId?: string }) => {
  const [allPicks, setAllPicks] = useState<any[]>([]);

  const fetchPicks = async () => {
    if (!sessionId) return;

    const { data } = await supabase
      .from("picks")
      .select(
        `
        id,
        round_number,
        pick_number,
        player:players(name, position),
        team:teams(team_name, primary_color)
      `
      )
      .eq("session_id", sessionId)
      .order("round_number", { ascending: true })
      .order("pick_number", { ascending: true });

    if (data) setAllPicks(data);
  };

  useEffect(() => {
    fetchPicks();
    const channel = supabase
      .channel(`ticker-updates-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "picks",
          filter: `session_id=eq.${sessionId}`,
        },
        () => fetchPicks()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const getLogoUrl = (teamName: string) => {
    if (!teamName)
      return "https://empireproleague.com/wp-content/uploads/2021/05/Empire-Logo-Official.png";
    const fileName = `${encodeURIComponent(teamName)}.png`;
    return `https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/${fileName}`;
  };

  if (!sessionId) return null;

  const TickerContent = () => (
    <>
      {allPicks.map((pick, index) => (
        <div key={`${pick.id}-${index}`} className="ticker-item">
          <div
            className="team-logo-badge"
            style={{ borderLeftColor: pick.team.primary_color }}
          >
            <img
              src={getLogoUrl(pick.team.team_name)}
              alt=""
              className="ticker-team-img"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://empireproleague.com/wp-content/uploads/2021/05/Empire-Logo-Official.png";
              }}
            />
          </div>

          <span className="ticker-player-name">{pick.player.name}</span>

          <div className="ticker-meta-group">
            <span className="ticker-pos">{pick.player.position}</span>
            <span className="ticker-pick-data">
              RD {pick.round_number} PK {pick.pick_number}
            </span>
          </div>

          <span className="ticker-divider">//</span>
        </div>
      ))}
      <span className="ticker-league-brand">
        EMPIRE BASEBALL LEAGUE • OFFICIAL 2026 DRAFT •
      </span>
    </>
  );

  return (
    <div className="draft-ticker-container">
      <div className="ticker-label">DRAFT TRACKER</div>

      <div className="ticker-scroll-area">
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: "-50%" }}
          transition={{
            repeat: Infinity,
            duration: allPicks.length > 5 ? allPicks.length * 5 : 30,
            ease: "linear",
          }}
          className="ticker-track"
        >
          {allPicks.length === 0 ? (
            <span className="ticker-waiting">
              AWAITING THE FIRST SELECTION...
            </span>
          ) : (
            <>
              <TickerContent />
              <TickerContent />
            </>
          )}
        </motion.div>
      </div>

      <style>{`
        .draft-ticker-container {
          background: #000;
          border-top: 4px solid #22565a;
          position: fixed;
          bottom: 0; left: 0;
          width: 100%;
          display: flex;
          align-items: center;
          height: 50px;
          overflow: hidden;
          z-index: 1000;
          box-shadow: 0 -5px 25px rgba(0,0,0,0.9);
          font-family: 'Oswald', sans-serif;
        }

        .ticker-label {
          background: #22565a;
          color: #f9eeda;
          padding: 0 25px;
          font-weight: 800;
          height: 100%;
          display: flex;
          align-items: center;
          z-index: 1001;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 2px;
          box-shadow: 15px 0 20px rgba(0,0,0,0.6);
          font-size: 0.9rem;
        }

        .ticker-scroll-area {
          flex: 1;
          overflow: hidden;
          height: 100%;
          background: #050505;
          position: relative;
          display: flex;
          align-items: center;
        }

        .ticker-track {
          display: flex;
          gap: 60px;
          align-items: center;
          height: 100%;
          white-space: nowrap;
          width: max-content;
          will-change: transform;
        }

        .ticker-item {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .team-logo-badge {
          background: #111;
          height: 30px;
          width: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
          border-left: 3px solid #22565a;
          padding: 2px;
        }

        .ticker-team-img {
          height: 85%;
          width: 85%;
          object-fit: contain;
        }

        /* --- UNIFIED TEXT SIZES --- */
        .ticker-player-name { 
          color: #f9eeda; 
          font-weight: 700; 
          font-size: 1.15rem; 
          text-transform: uppercase;
        }

        .ticker-pos {
          color: #22565a;
          font-weight: 800;
          font-size: 1.15rem; /* Matched to name size */
          text-transform: uppercase;
        }

        .ticker-meta-group {
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(34, 86, 90, 0.1);
          padding: 2px 12px;
          border-radius: 2px;
        }

        .ticker-pick-data {
          color: #666;
          font-weight: 500;
          font-size: 0.85rem; /* Kept slightly smaller to avoid clutter */
          text-transform: uppercase;
          padding-top: 2px; /* Visual alignment for the smaller font */
        }

        .ticker-divider { color: #22565a; font-weight: 900; opacity: 0.4; margin-right: 10px; }
        .ticker-league-brand { color: #22565a; font-weight: 800; opacity: 0.5; margin-right: 60px; }
        .ticker-waiting { color: #333; text-transform: uppercase; font-weight: 700; padding-left: 50px; }

        @media (max-width: 768px) {
          .draft-ticker-container {
            height: 40px;
          }
          .ticker-label {
            padding: 0 10px;
            font-size: 0.7rem;
            letter-spacing: 1px;
          }
          .ticker-player-name {
            font-size: 0.9rem;
          }
          .ticker-pos {
            font-size: 0.9rem;
          }
          .ticker-track {
            gap: 30px;
          }
          .ticker-pick-data {
            font-size: 0.7rem;
          }
          .ticker-meta-group {
            gap: 6px;
            padding: 1px 6px;
          }
          .team-logo-badge {
            height: 24px;
            width: 34px;
          }
        }
      `}</style>
    </div>
  );
};
